import { useEffect, useRef } from 'react';

import { getWebsiteUrlError } from './utils';
import { websiteValidationErrors } from './consts';

const ERROR_SHOW_DELAY_MS = 1500;

// Static #es-website-input lives outside React's render tree (only Part2 is portaled), so
// validation errors must be painted onto it directly — border color + message — mirroring
// what WebsiteInput/Field render in the normal flow. Showing an error while the user is
// actively typing is debounced the same 1500ms WebsiteInput uses; clearing a resolved error
// happens immediately. A value already typed before React mounted is shown immediately too,
// since the user has necessarily already stopped typing by the time this runs.
export const useEarlyShellWebsiteInput = (active, onWebsiteChange, onWebsiteValidate, i18n) => {
  const hasProcessedInitialValueRef = useRef(false);

  useEffect(() => {
    if (!active) return undefined;
    const input = document.getElementById('es-website-input');
    const errorEl = document.getElementById('es-website-error');
    const errorTextEl = errorEl && errorEl.querySelector('.es-website-error-text');
    if (!input) return undefined;
    let showErrorTimer = null;

    function renderError(errorKey) {
      if (errorKey && errorTextEl) { errorTextEl.textContent = i18n.getString(websiteValidationErrors[errorKey]); }
      if (errorEl) { errorEl.style.display = errorKey ? 'flex' : 'none'; }
      if (errorKey) {
        input.style.borderColor = '#d13438';
      } else {
        // Restore the resting per-side colors explicitly (static-early-shell-styles.ts) instead
        // of clearing style.borderColor to '' — the CSSOM borderColor shorthand setter removes
        // ALL FOUR border-*-color longhands when set to '', not just the one this call cares
        // about, wiping out the borderTop/Right/Bottom/Left colors React set inline. With
        // border-style still 'solid' but no color, the border falls back to currentColor (the
        // input's text color) — a dark, uniform 4-side border that never resolves once typing
        // starts, since this is the real post-handoff validator (no self-detach like interactive.ts).
        input.style.borderTopColor = '#d1d1d1';
        input.style.borderRightColor = '#d1d1d1';
        input.style.borderLeftColor = '#d1d1d1';
        input.style.borderBottomColor = '#616161';
      }
    }

    function handleValue(value, immediate) {
      const errorKey = getWebsiteUrlError(value);
      onWebsiteChange(value);
      onWebsiteValidate({ isValid: !errorKey, data: { website: value } });
      if (showErrorTimer) { clearTimeout(showErrorTimer); }
      if (!errorKey || immediate) { renderError(errorKey); return; }
      showErrorTimer = setTimeout(() => renderError(errorKey), ERROR_SHOW_DELAY_MS);
    }

    if (!hasProcessedInitialValueRef.current) {
      hasProcessedInitialValueRef.current = true;
      // #es-website-input is the real DOM element — whatever the user typed before React
      // mounted is already sitting in its value, no bridge relay needed.
      if (input.value) { handleValue(input.value, true); }
    }

    function onInput(e) { handleValue(e.target.value, false); }
    input.addEventListener('input', onInput);
    return () => {
      input.removeEventListener('input', onInput);
      if (showErrorTimer) { clearTimeout(showErrorTimer); }
    };
  }, [active, onWebsiteChange, onWebsiteValidate, i18n]);
};
